import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Drawer } from "vaul"
import { X } from "lucide-react"
import { VaulBottomSheetShell } from "@/shared/ui/VaulBottomSheetShell"
import { translatePath, useI18n } from "@/shared/i18n"
import type { AppLocale } from "@/shared/i18n/translations"
import { formatAppLocaleDateTimeShort } from "@/shared/lib/formatTournamentStartsAt"
import { cn } from "@/shared/lib/utils"
import { formatTonFromNano } from "@/shared/lib/pvpRouletteMath"
import TonIcon from "@/assets/icons/ton-icon.svg?react"
import TgStarIcon from "@/assets/icons/tg-star-icon.svg?react"
import { initiateWithdrawal } from "@/shared/api"
import type { UserGift } from "@/shared/api/types"
import { useBalance } from "@/entities/user"
import { giftKeys } from "@/entities/gift"
import { paymentHistoryKeys } from "@/entities/history"
import { makeErrorToast } from "@/shared/lib/toast-error"
import { AnalyticsEvent, trackAnalyticsEvent } from "@/shared/lib/analytics"
import { telegram } from "@/shared/lib/telegram"
import { getTelegramGiftViewerUrl } from "../lib/giftLinks"
import {
  formatGiftWithdrawalErrorLabel,
  readGiftWithdrawalErrorCode,
} from "../lib/giftWithdrawalError"

function tonFromNano(nano: number | null | undefined): number | null {
  if (typeof nano !== "number" || !Number.isFinite(nano)) return null
  return nano / 1e9
}

function getDisplayName(gift: UserGift): string {
  return gift.collection || gift.slug
}

interface GiftDetailsSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  gift: UserGift | null
  onRequestDeposit?: (shortfallTon: number) => void
  /**
   * When provided, shows a "Play with this gift" button. The caller decides what
   * happens (navigate to PvP from inventory, or just close all sheets when already
   * on the PvP screen). No bet is placed here — it only switches/closes.
   */
  onPlay?: () => void
  /**
   * When set, replaces the withdraw/play actions with a single green "Buy for X ⟁"
   * button (used by the marketplace flow). Price comes from `gift.assessed_price_nano`.
   */
  buyMode?: {
    onBuy: () => void
    isBuying?: boolean
  }
}

type Phase = "details" | "confirm" | "success"

export function GiftDetailsSheet({
  open,
  onOpenChange,
  gift,
  onRequestDeposit,
  onPlay,
  buyMode,
}: GiftDetailsSheetProps) {
  const { t, locale } = useI18n()
  const [phase, setPhase] = useState<Phase>("details")
  const queryClient = useQueryClient()

  // Parents clear `gift` the instant they close, which would unmount the content
  // mid-animation and make the drawer slide out empty. Latch the last gift so the
  // close animation plays with content intact.
  const [latchedGift, setLatchedGift] = useState<UserGift | null>(gift)
  useEffect(() => {
    if (gift) setLatchedGift(gift)
  }, [gift])

  useEffect(() => {
    if (!open) setPhase("details")
  }, [open])

  const displayGift = gift ?? latchedGift

  const withdrawMutation = useMutation({
    mutationFn: async (g: UserGift) =>
      initiateWithdrawal({
        asset_class: "TELEGRAM_GIFT",
        nft_id: g.id,
        amount: null,
        // Required by schema but ignored by core for gift withdrawals.
        recipient_key: "",
      }),
    onSuccess: (_data, g) => {
      trackAnalyticsEvent(AnalyticsEvent.GiftWithdrawInitiated, {
        gift_id: g.id,
      })
      void queryClient.invalidateQueries({ queryKey: giftKeys.all })
      void queryClient.invalidateQueries({
        queryKey: paymentHistoryKeys.infinite(),
      })
      setPhase("success")
    },
    onError: (err, g) => {
      console.error("[GiftDetailsSheet] withdrawal failed:", err)
      if (readGiftWithdrawalErrorCode(err) === "gift_transfer_hold") {
        void queryClient.invalidateQueries({ queryKey: giftKeys.all })
      }
      makeErrorToast({
        label: formatGiftWithdrawalErrorLabel(err, t, g, locale),
      })
    },
  })

  return (
    <Drawer.Root
      open={open}
      onOpenChange={onOpenChange}
      repositionInputs={false}
      modal={false}
    >
      <Drawer.Portal>
        <div
          aria-hidden
          data-vaul-overlay=""
          className="fixed inset-0 z-[200] bg-black/60"
          onPointerDown={(e) => {
            if (e.target === e.currentTarget) onOpenChange(false)
          }}
        />
        <VaulBottomSheetShell panelClassName="overflow-hidden rounded-t-[24px] bg-sheet">
          <Drawer.Handle className="mx-auto mb-0 mt-1.5 h-1 w-8 shrink-0 rounded-full bg-white/10" />
          <Drawer.Title className="sr-only">
            {displayGift ? getDisplayName(displayGift) : ""}
          </Drawer.Title>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-2 top-3 z-10 flex h-10 w-10 items-center justify-center text-white/90 transition-colors hover:text-white focus:outline-none"
            aria-label={translatePath("ui.close")}
          >
            <X className="h-6 w-6" />
          </button>
          {displayGift && (
            <motion.div
              layout
              transition={{ type: "spring", stiffness: 320, damping: 34 }}
              className="bg-sheet"
            >
            <AnimatePresence mode="popLayout" initial={false}>
              {phase === "details" && (
                <motion.div
                  key="details"
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="flex flex-col"
                >
                  <GiftSheetHeader gift={displayGift} />
                  <GiftAttributes gift={displayGift} />
                  {buyMode ? (
                    <GiftBuyCta
                      gift={displayGift}
                      onBuy={buyMode.onBuy}
                      isBuying={buyMode.isBuying}
                    />
                  ) : (
                    <GiftWithdrawCta
                      gift={displayGift}
                      onClick={() => setPhase("confirm")}
                      onPlay={onPlay}
                    />
                  )}
                </motion.div>
              )}

              {phase === "confirm" && (
                <motion.div
                  key="confirm"
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="flex flex-col"
                >
                  <GiftSheetHeader gift={displayGift} />
                  <ConfirmBody />
                  <ConfirmActions
                    gift={displayGift}
                    isSubmitting={withdrawMutation.isPending}
                    onCancel={() => setPhase("details")}
                    onSend={() => withdrawMutation.mutate(displayGift)}
                    onRequestDeposit={onRequestDeposit}
                  />
                </motion.div>
              )}

              {phase === "success" && (
                <motion.div
                  key="success"
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="flex flex-col"
                >
                  <SuccessBody
                    gift={displayGift}
                    onClose={() => onOpenChange(false)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            </motion.div>
          )}
        </VaulBottomSheetShell>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

// ── Header (image + name + #num) ────────────────────────────────────────────

function GiftSheetHeader({ gift }: { gift: UserGift }) {
  const name = getDisplayName(gift)
  const imageUrl = gift.large_image_url || gift.small_image_url

  return (
    <div className="flex w-full flex-col items-center gap-3 px-4 pt-12">
      <div
        className="h-[180px] w-[180px] overflow-hidden rounded-3xl bg-secondary-main bg-cover bg-center"
        style={{ backgroundImage: `url(${imageUrl})` }}
      />
      <div className="flex flex-col items-center gap-1">
        <span className="text-[22px] font-bold leading-7 tracking-[-0.5px] text-white">
          {name}
        </span>
        {typeof gift.num === "number" && (
          <span className="text-[15px] leading-5 tracking-[-0.5px] text-white/40">
            #{gift.num}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Attribute rows (Model / Background / Symbol) ─────────────────────────────

interface AttributeRow {
  label: string
  value: string
}

function buildAttributeRows(
  gift: UserGift,
  t: (key: string) => string
): AttributeRow[] {
  const rows: AttributeRow[] = []
  if (gift.model) rows.push({ label: t("giftDetail.attrModel"), value: gift.model })
  if (gift.background)
    rows.push({ label: t("giftDetail.attrBackground"), value: gift.background })
  if (gift.symbol)
    rows.push({ label: t("giftDetail.attrSymbol"), value: gift.symbol })
  return rows
}

function GiftAttributes({ gift }: { gift: UserGift }) {
  const { t } = useI18n()
  const rows = buildAttributeRows(gift, t)
  if (rows.length === 0) return <div className="h-4" />

  return (
    <div className="flex flex-col gap-2 px-4 pt-6">
      {rows.map((row) => (
        <div
          key={row.label}
          className="flex h-12 items-center justify-between rounded-2xl bg-gray-32 px-4"
        >
          <span className="text-[15px] font-medium leading-5 tracking-[-0.5px] text-white/60">
            {row.label}
          </span>
          <span className="text-[15px] font-medium leading-5 tracking-[-0.5px] text-white">
            {row.value}
          </span>
        </div>
      ))}
    </div>
  )
}

// ── Details-step CTA ─────────────────────────────────────────────────────────

function getUnavailableReasonText(
  gift: UserGift,
  t: (key: string, params?: Record<string, string | number>) => string,
  locale: AppLocale
): string | null {
  if (gift.withdrawal_available) return null
  switch (gift.withdrawal_unavailable_reason) {
    case "gift_status_not_available":
      return t("giftDetail.unavailableInRound")
    case "gift_transfer_hold": {
      if (typeof gift.withdrawable_at === "number" && gift.withdrawable_at > 0) {
        return t("giftDetail.unavailableHold", {
          date: formatAppLocaleDateTimeShort(gift.withdrawable_at, locale),
        })
      }
      return t("giftDetail.unavailableHoldNoDate")
    }
    case "gift_transfer_metadata_missing":
      return t("giftDetail.unavailableMetadata")
    default:
      return t("giftDetail.unavailableGeneric")
  }
}

function GiftBuyCta({
  gift,
  onBuy,
  isBuying = false,
}: {
  gift: UserGift
  onBuy: () => void
  isBuying?: boolean
}) {
  const { t } = useI18n()
  const price = formatTonFromNano(gift.assessed_price_nano)

  const viewOnTelegram = () => {
    telegram.openTelegramLink(getTelegramGiftViewerUrl(gift.slug))
  }

  return (
    <div className="flex flex-col gap-2 px-4 pb-4 pt-6">
      <button
        type="button"
        onClick={onBuy}
        disabled={isBuying}
        className={cn(
          "flex h-12 items-center justify-center gap-1.5 rounded-2xl text-[16px] font-bold leading-[19px] tracking-[-0.5px] text-black-100 transition-transform active:scale-[0.99]",
          isBuying ? "cursor-not-allowed bg-[#00FE74]/50" : "bg-[#00FE74]"
        )}
      >
        <span>{t("marketplace.buyFor", { amount: price })}</span>
        <TonIcon className="size-3.5 text-black-100" />
      </button>
      <button
        type="button"
        onClick={viewOnTelegram}
        className="flex h-12 items-center justify-center rounded-2xl bg-white/10 text-[16px] font-medium leading-[19px] tracking-[-0.5px] text-white transition-transform active:scale-[0.99]"
      >
        {t("marketplace.viewOnTelegram")}
      </button>
    </div>
  )
}

function GiftWithdrawCta({
  gift,
  onClick,
  onPlay,
}: {
  gift: UserGift
  onClick: () => void
  onPlay?: () => void
}) {
  const { t, locale } = useI18n()
  const hint = getUnavailableReasonText(gift, t, locale)
  const disabled = !gift.withdrawal_available
  const playDisabled = !gift.available_for_bet

  return (
    <div className="flex flex-col gap-2 px-4 pb-4 pt-6">
      {onPlay && (
        <button
          type="button"
          onClick={onPlay}
          disabled={playDisabled}
          className="flex h-12 items-center justify-center rounded-2xl bg-[#00FE74] text-[16px] font-bold leading-[19px] tracking-[-0.5px] text-black-100 transition-transform active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {t("giftDetail.playWithGift")}
        </button>
      )}
      {hint && (
        <span className="text-center text-[13px] leading-4 tracking-[-0.3px] text-white/50">
          {hint}
        </span>
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          "flex h-12 items-center justify-center rounded-2xl text-[16px] font-bold leading-[19px] tracking-[-0.5px] transition-transform active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40",
          onPlay
            ? "bg-white/10 text-white"
            : "bg-[#00FE74] text-black-100"
        )}
      >
        {t("giftDetail.withdraw")}
      </button>
    </div>
  )
}

// ── Confirm-step body & actions ──────────────────────────────────────────────

function ConfirmBody() {
  const { t } = useI18n()

  return (
    <div className="flex flex-col gap-3 px-4 pt-8">
      <h2 className="text-center text-[24px] font-bold leading-7 tracking-[-0.5px] text-white">
        {t("giftDetail.confirmTitle")}
      </h2>
      <div className="mt-2 rounded-2xl bg-warning-10 px-4 py-3 text-center text-[14px] leading-[18px] tracking-[-0.3px] text-warning-100">
        {t("giftDetail.privacyWarning")}
      </div>
      <div className="rounded-2xl bg-warning-10 px-4 py-3 text-center text-[14px] leading-[18px] tracking-[-0.3px] text-warning-100">
        {t("giftDetail.delayWarning")}
      </div>
    </div>
  )
}

function ConfirmActions({
  gift,
  isSubmitting,
  onCancel,
  onSend,
  onRequestDeposit,
}: {
  gift: UserGift
  isSubmitting: boolean
  onCancel: () => void
  onSend: () => void
  onRequestDeposit?: (shortfallTon: number) => void
}) {
  const { t } = useI18n()
  const { data: balanceData } = useBalance()

  const rawBalance = Number(balanceData?.balance?.amount ?? "0")
  const balanceTon = Number.isFinite(rawBalance) ? rawBalance : 0

  const feeTon = tonFromNano(gift.withdrawal_fee_nano_ton)
  const feeUnknown = feeTon === null

  // Tolerate single-nanoton FP drift when comparing balance to fee.
  const BALANCE_EPSILON_TON = 1e-9
  const hasFunds = !feeUnknown && balanceTon + BALANCE_EPSILON_TON >= feeTon
  const shortfallTon = feeUnknown
    ? 0
    : Math.max(0, feeTon - balanceTon)

  const transferStars = gift.transfer_stars ?? null

  return (
    <div className="flex flex-col gap-3 px-4 pb-4 pt-4">
      {!feeUnknown && !hasFunds && (
        <div className="rounded-2xl bg-[#FF3333]/10 p-4 text-center text-[14px] leading-[18px] tracking-[-0.5px] text-[#FF3333]">
          {t("giftDetail.insufficientFunds")}
        </div>
      )}

      <p className="flex items-center justify-center gap-1 text-center text-[13px] leading-4 tracking-[-0.3px] text-white/50">
        <span>{t("giftDetail.rateHint")}</span>
        {transferStars && (
          <span className="inline-flex items-center gap-0.5">
            {transferStars}
            <TgStarIcon className="size-3.5" />
          </span>
        )}
      </p>

      {hasFunds ? (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex h-12 w-[72px] shrink-0 items-center justify-center rounded-2xl bg-gray-100 text-[16px] font-bold leading-[19px] tracking-[-0.5px] text-white transition-transform active:scale-[0.98] disabled:opacity-50"
          >
            {t("giftDetail.no")}
          </button>
          <button
            type="button"
            onClick={onSend}
            disabled={isSubmitting}
            className={cn(
              "flex h-12 flex-1 items-center justify-center gap-1.5 rounded-2xl text-[16px] font-bold leading-[19px] tracking-[-0.5px] text-black-100 transition-transform active:scale-[0.99]",
              isSubmitting
                ? "cursor-not-allowed bg-[#00FE74]/40"
                : "bg-[#00FE74]"
            )}
          >
            <span>{t("giftDetail.sendCta")}</span>
            <span>{formatTonFromNano(gift.withdrawal_fee_nano_ton, { placeholder: "—" })}</span>
            <TonIcon className="size-3.5 text-black-100" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onRequestDeposit?.(shortfallTon)}
          disabled={feeUnknown}
          className={cn(
            "flex h-12 items-center justify-center gap-1.5 rounded-2xl text-[16px] font-bold leading-[19px] tracking-[-0.5px] text-black-100 transition-transform active:scale-[0.99]",
            feeUnknown
              ? "cursor-not-allowed bg-[#00FE74]/40"
              : "bg-[#00FE74]"
          )}
        >
          <span>{t("giftDetail.depositCta")}</span>
          {!feeUnknown && shortfallTon > 0 && (
            <>
              <span>{formatTonFromNano(shortfallTon * 1e9, { placeholder: "—" })}</span>
              <TonIcon className="size-3.5 text-black-100" />
            </>
          )}
        </button>
      )}
    </div>
  )
}

// ── Success step ────────────────────────────────────────────────────────────

function SuccessBody({
  gift,
  onClose,
}: {
  gift: UserGift
  onClose: () => void
}) {
  const { t } = useI18n()
  const imageUrl = gift.large_image_url || gift.small_image_url

  return (
    <div className="flex w-full flex-col items-center gap-4 px-4 pb-4 pt-12">
      <div
        className="h-[180px] w-[180px] overflow-hidden rounded-3xl bg-secondary-main bg-cover bg-center"
        style={{ backgroundImage: `url(${imageUrl})` }}
      />
      <h2 className="text-center text-[24px] font-bold leading-7 tracking-[-0.5px] text-white">
        {t("giftDetail.successTitle")}
      </h2>
      <p className="max-w-[326px] text-center text-[15px] leading-5 tracking-[-0.3px] text-white/60">
        {t("giftDetail.successBody")}
      </p>
      <button
        type="button"
        onClick={onClose}
        className="mt-4 flex h-12 w-full items-center justify-center rounded-2xl bg-[#00FE74] text-[16px] font-bold leading-[19px] tracking-[-0.5px] text-black-100 transition-transform active:scale-[0.99]"
      >
        {t("giftDetail.successCta")}
      </button>
    </div>
  )
}
